import clsx from 'clsx';

/**
 * Modern Card Component
 * Beautiful cards with soft shadows and smooth transitions
 */

export default function Card({
  children,
  variant = 'default',
  padding = 'default',
  className = '',
  onClick,
  ...props
}) {
  const baseClasses = 'bg-white rounded-2xl shadow-soft-lg border border-slate-200/60 transition-all duration-300';
  
  const variantClasses = {
    default: '',
    hover: 'hover:shadow-soft-xl hover:scale-[1.01] cursor-pointer',
    clickable: 'hover:shadow-soft-xl hover:border-primary-300 hover:scale-[1.01] cursor-pointer',
    glass: 'bg-white/70 backdrop-blur-xl border-white/20',
  };

  const paddingClasses = {
    none: 'p-0',
    sm: 'p-4',
    default: 'p-6',
    lg: 'p-8',
  };

  return (
    <div
      className={clsx(
        baseClasses,
        variantClasses[variant],
        paddingClasses[padding],
        className
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * CardHeader Component
 * Modern card header with gradient text option
 */
export function CardHeader({ title, subtitle, action, children, gradient = false }) {
  return (
    <div className="flex items-start justify-between mb-6">
      <div className="flex-1">
        {title && (
          <h3 className={clsx(
            'text-xl font-semibold',
            gradient ? 'text-gradient-primary' : 'text-slate-900'
          )}>
            {title}
          </h3>
        )}
        {subtitle && (
          <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
        )}
        {children}
      </div>
      {action && (
        <div className="ml-4 flex-shrink-0">{action}</div>
      )}
    </div>
  );
}

/**
 * CardBody Component
 */
export function CardBody({ children, className = '' }) {
  return (
    <div className={clsx('text-sm text-slate-700', className)}>
      {children}
    </div>
  );
}

/**
 * CardFooter Component
 */
export function CardFooter({ children, className = '' }) {
  return (
    <div className={clsx('mt-6 pt-6 border-t border-slate-200', className)}>
      {children}
    </div>
  );
}